import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Download, Play, X, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const mb = (n) => (n / 1048576).toFixed(1);

// Downloads a large authenticated file with a visible progress bar (streamed fetch),
// then saves it. "Play in browser" streams the video instead of waiting for the file.
export function MediaDownloadDialog({ item, onClose }) {
  const [received, setReceived] = useState(0);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("idle");
  const [playing, setPlaying] = useState(false);
  const [copied, setCopied] = useState(false);
  const abortRef = useRef(null);
  const url = item ? `${API}${item.path}` : "";

  useEffect(() => {
    if (!item) return;
    setReceived(0); setTotal(0); setStatus("downloading"); setPlaying(false);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    (async () => {
      try {
        const res = await fetch(url, { credentials: "include", signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const len = Number(res.headers.get("content-length") || 0);
        setTotal(len);
        const reader = res.body.getReader();
        const chunks = [];
        let got = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value); got += value.length; setReceived(got);
        }
        const blob = new Blob(chunks, { type: item.mime || "video/mp4" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob); a.download = item.filename;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 3000);
        setStatus("done");
      } catch (e) {
        if (e.name !== "AbortError") setStatus("error");
      }
    })();
    return () => ctrl.abort();
  }, [item, url]);

  const copyShare = async () => {
    try {
      const { data } = await api.get("/admin/marketing/share-links");
      await navigator.clipboard.writeText(data[item.shareKey]);
      setCopied(true); toast.success("Share link copied — anyone with the link can view it");
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Could not copy the link"); }
  };
  const pct = total ? Math.round((received / total) * 100) : 0;
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) { abortRef.current?.abort(); onClose(); } }}>
      <DialogContent data-testid="media-download-dialog" className={playing ? "max-w-4xl" : "max-w-md"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Download className="h-4 w-4" /> {item?.title}</DialogTitle>
          <DialogDescription>
            {status === "downloading" && "Downloading — this is a large file, your browser will save it when it completes."}
            {status === "done" && "Saved to your downloads folder."}
            {status === "error" && "The download failed. Please try again."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Progress value={status === "done" ? 100 : pct} data-testid="media-download-progress" />
          <div className="flex justify-between text-xs text-muted-foreground" data-testid="media-download-stats">
            <span>{mb(received)} MB{total ? ` of ${mb(total)} MB` : ""}</span>
            <span>{status === "done" ? "100%" : total ? `${pct}%` : "…"}</span>
          </div>
          {playing && (
            <video src={`${url}${url.includes("?") ? "&" : "?"}inline=1`} controls autoPlay className="w-full rounded-md bg-black" data-testid="media-player" />
          )}
          <div className="flex gap-2 justify-end flex-wrap">
            {item?.shareKey && (
              <Button variant="outline" onClick={copyShare} data-testid="media-share-btn" className="gap-2">{copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />} Copy share link</Button>
            )}
            {item?.playable && !playing && (
              <Button variant="outline" onClick={() => setPlaying(true)} data-testid="media-play-btn" className="gap-2"><Play className="h-4 w-4" /> Play in browser</Button>
            )}
            <Button variant={status === "done" ? "default" : "outline"} onClick={() => { abortRef.current?.abort(); onClose(); }} data-testid="media-download-close" className="gap-2">
              <X className="h-4 w-4" /> {status === "downloading" ? "Cancel" : "Close"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
